/**
 * 订单管理 API / Order Management APIs
 * @module services/order
 */

import api from './api';

/**
 * 创建订单 / Create Order
 * @param {Object} orderData - 订单数据
 * @param {string} orderData.sourceChannel - 来源渠道 (SELF/AGENT/DELIVERY)
 * @param {number} orderData.customerId - 客户 ID
 * @param {string} orderData.customerName - 客户名称
 * @param {string} orderData.customerPhone - 客户电话
 * @param {number} orderData.packageId - 批发报价 ID（可选）
 * @param {Array} orderData.items - 订单项列表
 * @param {number} orderData.subtotal - 小计
 * @param {number} orderData.gstRate - GST 税率
 * @param {number} orderData.gstAmount - GST 税额
 * @param {number} orderData.totalAmount - 总金额
 * @param {string} orderData.paymentMethod - 支付方式
 * @param {string} orderData.deliveryMethod - 配送方式
 * @param {string} orderData.deliveryAddress - 配送地址
 * @param {string} orderData.deliveryRegionCode - 配送区域编码
 * @param {string} orderData.remark - 备注
 * @returns {Promise}
 */
export const createOrder = (orderData) => {
  return api.post('/orders', orderData);
};

/**
 * 获取订单列表 / Get Order List
 * @param {Object} params - 查询参数
 * @param {string} params.orderType - 订单类型 (SELF/AGENT/DELIVERY)
 * @param {string} params.sourceChannel - 来源渠道
 * @param {number} params.page - 页码
 * @param {number} params.size - 每页数量
 * @returns {Promise}
 */
export const getOrders = (params = {}) => {
  return api.get('/orders', { params });
};

/**
 * 获取订单详情 / Get Order Detail
 * @param {number} id - 订单 ID
 * @returns {Promise}
 */
export const getOrderDetail = (id) => {
  return api.get(`/orders/${id}`);
};

/**
 * 获取代客下单列表 / Get Agent Orders
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.size - 每页数量
 * @returns {Promise}
 */
export const getAgentOrders = (params = {}) => {
  return api.get('/orders/agent-orders', { params });
};

/**
 * 订单发货 / Ship Order
 * @param {number} id - 订单 ID
 * @returns {Promise}
 */
export const shipOrder = (id) => {
  return api.put(`/orders/${id}/ship`);
};

/**
 * 取消订单 / Cancel Order
 * @param {number} id - 订单 ID
 * @param {Object} data - 取消原因
 * @param {string} data.reason - 取消原因
 * @param {string} data.description - 详细描述
 * @returns {Promise}
 */
export const cancelOrder = (id, data) => {
  return api.post(`/orders/${id}/cancel`, data);
};

/**
 * 获取订单时间轴 / Get Order Timeline
 * @param {number} id - 订单 ID
 * @returns {Promise}
 */
export const getOrderTimeline = (id) => {
  return api.get(`/orders/${id}/timeline`);
};

/**
 * 开始打包 / Start Packing
 * @param {number} id - 订单 ID
 * @returns {Promise}
 */
export const startPacking = (id) => {
  return api.post(`/orders/${id}/pack`);
};

/**
 * 打包完成 / Complete Packing
 * @param {number} id - 订单 ID
 * @returns {Promise}
 */
export const completePacking = (id) => {
  return api.post(`/orders/${id}/pack-complete`);
};

/**
 * 确认送达 / Confirm Delivery
 * @param {number} id - 订单 ID
 * @returns {Promise}
 */
export const confirmDelivery = (id) => {
  return api.post(`/orders/${id}/deliver`);
};

export default {
  createOrder,
  getOrders,
  getOrderDetail,
  getAgentOrders,
  shipOrder,
  cancelOrder,
  getOrderTimeline,
  startPacking,
  completePacking,
  confirmDelivery
};
